import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatBytes } from '@/lib/format-bytes';
import { ResourceRangePanel } from '@/routes/admin/resource-usage/RangePanel';
import type { GpuProcess, GpuSnapshot, ResourceSnapshot } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ChevronDown,
  Cpu,
  CpuIcon,
  HardDrive,
  MemoryStick,
  Microchip,
  Pause,
  Play,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const REFRESH_INTERVAL_KEY = 'admin.resourceUsage.refreshIntervalSec';
const SELECTED_HOST_KEY = 'admin.resourceUsage.selectedHostId';
const HISTORY_WINDOW_SEC = 300; // 5분치 sparkline

const REFRESH_OPTIONS = [3, 5, 10, 30] as const;

type RefreshOption = (typeof REFRESH_OPTIONS)[number];

function loadRefreshInterval(): RefreshOption {
  if (typeof window === 'undefined') return 5;
  const raw = window.localStorage.getItem(REFRESH_INTERVAL_KEY);
  const n = raw ? Number.parseInt(raw, 10) : 5;
  return (REFRESH_OPTIONS as readonly number[]).includes(n) ? (n as RefreshOption) : 5;
}

function saveRefreshInterval(v: RefreshOption | 'pause'): void {
  if (typeof window === 'undefined') return;
  if (v === 'pause') {
    window.localStorage.setItem(REFRESH_INTERVAL_KEY, '0');
  } else {
    window.localStorage.setItem(REFRESH_INTERVAL_KEY, String(v));
  }
}

function loadPaused(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(REFRESH_INTERVAL_KEY) === '0';
}

// 프로세스 식별자로 deterministic 색상 — 같은 프로세스(pid+name)는 항상 같은 색.
const PROCESS_COLORS = [
  '#f97316', // orange-500
  '#14b8a6', // teal-500
  '#06b6d4', // cyan-500
  '#facc15', // yellow-400
  '#ec4899', // pink-500
  '#a855f7', // purple-500
  '#22c55e', // green-500
  '#3b82f6', // blue-500
];

function hashColor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) {
    h = (h * 31 + key.charCodeAt(i)) & 0xffffffff;
  }
  const fallback = PROCESS_COLORS[0] ?? '#f97316';
  return PROCESS_COLORS[Math.abs(h) % PROCESS_COLORS.length] ?? fallback;
}

function formatPct(value: number | null, fractionDigits = 1): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(fractionDigits)} %`;
}

// ───────────────────────────────────────────────────────────────
// Top-level
// ───────────────────────────────────────────────────────────────

export function AdminResourceUsage() {
  const hostsQuery = useQuery({
    queryKey: ['admin', 'resource-hosts'],
    queryFn: () => api.adminListResourceHosts(),
  });
  const enabledHosts = useMemo(
    () => hostsQuery.data?.hosts.filter((h) => h.enabled) ?? [],
    [hostsQuery.data],
  );

  const [selectedHostId, setSelectedHostId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(SELECTED_HOST_KEY);
  });

  // 호스트 목록 로드 후 / 선택된 호스트가 사라지면 첫 활성 호스트로 자동 선택.
  useEffect(() => {
    if (!enabledHosts.length) return;
    const exists = selectedHostId && enabledHosts.some((h) => h.id === selectedHostId);
    if (!exists) {
      const first = enabledHosts[0]?.id ?? null;
      setSelectedHostId(first);
      if (first && typeof window !== 'undefined') {
        window.localStorage.setItem(SELECTED_HOST_KEY, first);
      }
    }
  }, [enabledHosts, selectedHostId]);

  const [paused, setPaused] = useState(loadPaused);
  const [intervalSec, setIntervalSec] = useState<RefreshOption>(loadRefreshInterval);

  const refetchInterval = paused ? false : intervalSec * 1000;

  return (
    <PageShell
      header={
        <PageHeader
          title="리소스 사용 현황"
          description="AI 서버의 GPU / CPU / Disk 등 리소스 사용 현황을 확인할 수 있습니다."
          actions={
            <div className="flex items-center gap-2">
              {enabledHosts.length > 0 ? (
                <div className="relative">
                  <select
                    value={selectedHostId ?? ''}
                    onChange={(e) => {
                      const id = e.target.value || null;
                      setSelectedHostId(id);
                      if (id && typeof window !== 'undefined') {
                        window.localStorage.setItem(SELECTED_HOST_KEY, id);
                      }
                    }}
                    disabled={enabledHosts.length === 1}
                    aria-label="모니터링 대상 호스트 선택"
                    className="h-9 appearance-none rounded-md border border-border bg-background pl-3 pr-8 text-sm disabled:opacity-70 disabled:cursor-default"
                  >
                    {enabledHosts.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-muted" />
                </div>
              ) : null}
            </div>
          }
        />
      }
    >
      {hostsQuery.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : hostsQuery.error ? (
        <p className="text-sm text-destructive">
          호스트 목록을 불러올 수 없습니다:{' '}
          {hostsQuery.error instanceof ApiError ? hostsQuery.error.message : '알 수 없는 오류'}
        </p>
      ) : enabledHosts.length === 0 ? (
        <EmptyState />
      ) : selectedHostId ? (
        <HostDashboard
          hostId={selectedHostId}
          refetchInterval={refetchInterval}
          refreshControls={
            <RefreshControls
              paused={paused}
              intervalSec={intervalSec}
              onTogglePause={() => {
                const next = !paused;
                setPaused(next);
                saveRefreshInterval(next ? 'pause' : intervalSec);
              }}
              onChangeInterval={(v) => {
                setIntervalSec(v);
                setPaused(false);
                saveRefreshInterval(v);
              }}
            />
          }
        />
      ) : null}
    </PageShell>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-border rounded-2xl p-10 text-center">
      <p className="text-sm text-foreground-muted">
        활성화된 호스트가 없습니다.{' '}
        <Link to="/admin/resource-hosts" className="text-accent hover:underline">
          호스트 관리 페이지
        </Link>{' '}
        에서 등록해주세요.
      </p>
    </div>
  );
}

function RefreshControls({
  paused,
  intervalSec,
  onTogglePause,
  onChangeInterval,
}: {
  paused: boolean;
  intervalSec: RefreshOption;
  onTogglePause: () => void;
  onChangeInterval: (v: RefreshOption) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={onTogglePause}
        aria-label={paused ? '재개' : '일시정지'}
      >
        {paused ? <Play className="size-3.5" /> : <Pause className="size-3.5" />}
        <span className="text-xs">{paused ? '일시정지' : '폴링 중'}</span>
      </Button>
      <div className="relative">
        <select
          value={intervalSec}
          onChange={(e) => onChangeInterval(Number.parseInt(e.target.value, 10) as RefreshOption)}
          disabled={paused}
          aria-label="자동 새로고침 주기"
          className="h-8 appearance-none rounded-md border border-border bg-background pl-2 pr-7 text-xs disabled:opacity-50"
        >
          {REFRESH_OPTIONS.map((v) => (
            <option key={v} value={v}>
              {v}초
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-3 -translate-y-1/2 text-foreground-muted" />
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// 호스트별 대시보드
// ───────────────────────────────────────────────────────────────

// 클라이언트 링버퍼 최대 포인트 수 — 5초 × 60 = 5분, 30초 × 60 = 30분.
const CLIENT_HISTORY_CAPACITY = 60;

function HostDashboard({
  hostId,
  refetchInterval,
  refreshControls,
}: {
  hostId: string;
  refetchInterval: false | number;
  refreshControls: React.ReactNode;
}) {
  const latestQuery = useQuery({
    queryKey: ['admin', 'resource-metrics-latest', hostId],
    queryFn: () => api.adminGetResourceMetricsLatest(hostId),
    refetchInterval,
    refetchIntervalInBackground: false,
  });
  // 초기 sparkline 데이터는 서버 히스토리에서 한 번만 가져오고,
  // 이후 폴링 동안엔 latest 응답을 누적해 클라이언트가 직접 관리한다.
  // 매 5초마다 100KB 짜리 history 응답을 또 받지 않아 네트워크/CPU 비용 절감.
  const initialHistoryQuery = useQuery({
    queryKey: ['admin', 'resource-metrics-history', hostId, HISTORY_WINDOW_SEC],
    queryFn: () => api.adminGetResourceMetricsHistory(hostId, HISTORY_WINDOW_SEC),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const [history, setHistory] = useState<ResourceSnapshot[]>([]);

  // 호스트 변경 시 버퍼 리셋. setHistory 는 stable 이라 deps 가 hostId 만이어도 의도된 동작.
  // biome-ignore lint/correctness/useExhaustiveDependencies: hostId 변경을 reset 트리거로 사용
  useEffect(() => {
    setHistory([]);
  }, [hostId]);

  // 초기 히스토리 1회 로드. latestQuery 가 먼저 도착해 prev 에 1점이 들어가 있어도
  // collectedAt 기준 dedupe + 시간순 정렬로 안전하게 병합한다 (ISO-8601 문자열이라 사전순 = 시간순).
  useEffect(() => {
    const snapshots = initialHistoryQuery.data?.snapshots;
    if (!snapshots) return;
    setHistory((prev) => {
      const seen = new Set(prev.map((s) => s.collectedAt));
      const merged = [...snapshots.filter((s) => !seen.has(s.collectedAt)), ...prev];
      merged.sort((a, b) => a.collectedAt.localeCompare(b.collectedAt));
      return merged.slice(-CLIENT_HISTORY_CAPACITY);
    });
  }, [initialHistoryQuery.data]);

  // 매 polling latest 결과를 링버퍼에 append. collectedAt 중복은 무시.
  const latestSnapshot = latestQuery.data?.snapshot ?? null;
  useEffect(() => {
    if (!latestSnapshot) return;
    setHistory((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.collectedAt === latestSnapshot.collectedAt) return prev;
      const next = prev.length >= CLIENT_HISTORY_CAPACITY ? prev.slice(1) : prev.slice();
      next.push(latestSnapshot);
      return next;
    });
  }, [latestSnapshot]);

  // initialHistory 도 같이 기다린다 — 첫 페인트 시점에 sparkline 이 즉시 그려지도록.
  if (latestQuery.isLoading || initialHistoryQuery.isLoading) {
    return <p className="text-sm text-foreground-muted">불러오는 중…</p>;
  }
  if (latestQuery.error) {
    return (
      <p className="text-sm text-destructive">
        스냅샷을 불러올 수 없습니다:{' '}
        {latestQuery.error instanceof ApiError ? latestQuery.error.message : '알 수 없는 오류'}
      </p>
    );
  }
  const latest = latestSnapshot;
  const status = latestQuery.data?.status ?? 'pending';

  if (!latest) {
    return (
      <div className="border border-dashed border-border rounded-2xl p-10 text-center space-y-3">
        <p className="text-sm text-foreground-muted">
          {status === 'error'
            ? '호스트에서 데이터를 수집할 수 없습니다.'
            : '첫 스냅샷을 기다리고 있습니다…'}
        </p>
        {latestQuery.data?.lastError ? (
          <p className="text-xs text-destructive">{latestQuery.data.lastError}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <SectionHeader collectedAt={latest.collectedAt} refreshControls={refreshControls} />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {latest.gpus.map((gpu) => (
            <GpuCard key={gpu.index} gpu={gpu} history={history} />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <CpuUtilCard latest={latest} history={history} />
          <CpuTempCard latest={latest} history={history} />
          <MemoryCard latest={latest} history={history} />
          <DiskCard latest={latest} history={history} />
        </div>
      </div>
      <ResourceRangePanel hostId={hostId} />
    </div>
  );
}

function SectionHeader({
  collectedAt,
  refreshControls,
}: {
  collectedAt: string;
  refreshControls?: React.ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold">실시간 리소스 사용 현황</h2>
        <p className="text-xs text-foreground-muted mt-0.5">
          호스트에서 수집한 최신 스냅샷. 각 메트릭 옆 sparkline 은 최근 5분치 변화를 보여줍니다.
        </p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <p className="text-xs text-foreground-muted whitespace-nowrap">
          {new Date(collectedAt).toLocaleString('ko-KR')} 기준
        </p>
        {refreshControls}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Card primitives
// ───────────────────────────────────────────────────────────────

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={cn(
        'bg-surface border border-border rounded-2xl p-5 flex flex-col gap-3',
        className,
      )}
    >
      {children}
    </section>
  );
}

function CardHeader({
  title,
  subtitle,
  Icon,
}: {
  title: string;
  subtitle: string;
  Icon: typeof Cpu;
}) {
  return (
    <header className="flex items-start justify-between">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs text-foreground-muted mt-0.5">{subtitle}</p>
      </div>
      <Icon className="size-5 text-foreground-muted shrink-0" />
    </header>
  );
}

interface MetricRowProps {
  label: string;
  value: string;
  sparkline: number[];
  timestamps?: string[];
  formatValue?: (v: number) => string;
  tickSize?: number;
}

function MetricRow({ label, value, sparkline, timestamps, formatValue, tickSize }: MetricRowProps) {
  return (
    <div className="grid grid-cols-[60px_1fr_auto] items-center gap-3 text-xs">
      <span className="text-foreground-muted">{label}</span>
      <Sparkline
        values={sparkline}
        timestamps={timestamps}
        formatValue={formatValue}
        tickSize={tickSize}
      />
      {/* "999.9 °C" 같은 최악 표기까지 줄바꿈 없이 들어가도록 최소 폭 고정 */}
      <span className="font-medium tabular-nums whitespace-nowrap text-right min-w-15">
        {value}
      </span>
    </div>
  );
}

function Sparkline({
  values,
  timestamps,
  color,
  formatValue = (v: number) => v.toFixed(1),
  tickSize,
}: {
  values: number[];
  timestamps?: string[];
  color?: string;
  formatValue?: (v: number) => string;
  /**
   * 값을 이 단위 (예: 0.1) 로 반올림한 뒤 그리기. 표시값과 그래프 위치를 일치시키는 용도.
   * (소수점 1자리만 노출하는 메트릭에서 미세 변동이 다른 점으로 그려져 어색해 보이는 현상 방지.)
   */
  tickSize?: number;
}) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (values.length === 0) {
    return <div className="h-5" />;
  }
  const width = 100;
  const height = 20;
  const drawValues = tickSize ? values.map((v) => Math.round(v / tickSize) * tickSize) : values;
  const min = Math.min(...drawValues);
  const max = Math.max(...drawValues);
  const range = max - min || 1;
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const xOf = (i: number) => i * stepX;
  const yOf = (v: number) => height - ((v - min) / range) * height;
  const points = drawValues.map((v, i) => `${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ');

  const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width === 0) return;
    const relX = ((e.clientX - rect.left) / rect.width) * width;
    const idx = Math.max(0, Math.min(values.length - 1, Math.round(relX / stepX)));
    setHoverIdx(idx);
  };

  // tooltip 표기는 원본값 기준, 마커 위치(yOf)는 양자화된 값 기준 — 라인과 마커가 정확히 일치.
  const hoverValue = hoverIdx !== null ? values[hoverIdx] : undefined;
  const hoverDrawValue = hoverIdx !== null ? drawValues[hoverIdx] : undefined;
  const hoverAt = hoverIdx !== null ? timestamps?.[hoverIdx] : undefined;
  // tooltip 가 화면 좌/우 경계에 가까울 때 translateX 비율을 조정해 잘림 방지.
  let tooltipTransform = 'translateX(-50%)';
  if (hoverIdx !== null) {
    const ratio = xOf(hoverIdx) / width;
    if (ratio < 0.15) tooltipTransform = 'translateX(0%)';
    else if (ratio > 0.85) tooltipTransform = 'translateX(-100%)';
  }

  return (
    <div className="relative h-5 w-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="h-5 w-full text-foreground-muted block"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIdx(null)}
        role="presentation"
      >
        <polyline
          fill="none"
          stroke={color ?? 'currentColor'}
          strokeWidth="1"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={points}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {hoverIdx !== null && hoverValue !== undefined && hoverDrawValue !== undefined ? (
        <>
          {/* hover 위치 마커 — 컨테이너 비율 좌표로 div 배치 (SVG 좌표계의 stretch 영향 회피) */}
          <span
            className="pointer-events-none absolute size-1.5 rounded-full"
            style={{
              left: `${(xOf(hoverIdx) / width) * 100}%`,
              top: `${(yOf(hoverDrawValue) / height) * 100}%`,
              transform: 'translate(-50%, -50%)',
              backgroundColor: color ?? 'currentColor',
            }}
          />
          <span
            className="pointer-events-none absolute bottom-full mb-1 px-2 py-1 rounded-md border border-border bg-surface text-foreground text-[10px] whitespace-nowrap z-20 shadow-lg"
            style={{
              left: `${(xOf(hoverIdx) / width) * 100}%`,
              transform: tooltipTransform,
            }}
          >
            {hoverAt
              ? `${new Date(hoverAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })} · `
              : ''}
            {formatValue(hoverValue)}
          </span>
        </>
      ) : null}
    </div>
  );
}

// VRAM 스택 프로그레스 바 (프로세스별 색상).
function VramStackBar({
  totalBytes,
  processes,
}: {
  totalBytes: number;
  processes: GpuProcess[];
}) {
  if (totalBytes <= 0) return <div className="h-2 bg-surface-muted rounded-full" />;
  return (
    <div className="h-2 w-full bg-surface-muted rounded-full overflow-hidden flex">
      {processes.map((p) => {
        const pct = (p.usedMemoryBytes / totalBytes) * 100;
        if (pct <= 0) return null;
        return (
          <div
            key={`${p.pid}-${p.name}`}
            style={{ width: `${pct}%`, backgroundColor: hashColor(`${p.name}-${p.identifier}`) }}
            className="h-full"
          />
        );
      })}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// GPU Card
// ───────────────────────────────────────────────────────────────

function GpuCard({ gpu, history }: { gpu: GpuSnapshot; history: ResourceSnapshot[] }) {
  const tempHist = history.map((s) => s.gpus.find((g) => g.index === gpu.index)?.temperatureC ?? 0);
  const powerHist = history.map((s) => s.gpus.find((g) => g.index === gpu.index)?.powerW ?? 0);
  const utilHist = history.map(
    (s) => s.gpus.find((g) => g.index === gpu.index)?.utilizationPct ?? 0,
  );
  const historyTimestamps = history.map((s) => s.collectedAt);
  const vramPct = gpu.memoryTotalBytes > 0 ? (gpu.memoryUsedBytes / gpu.memoryTotalBytes) * 100 : 0;
  const freeBytes = Math.max(0, gpu.memoryTotalBytes - gpu.memoryUsedBytes);
  // 사용량 내림차순으로 정렬 — 범례 리스트와 VRAM 스택 바 색상 순서가 일치하도록 동일 배열 사용.
  const sortedProcesses = [...gpu.processes].sort((a, b) => b.usedMemoryBytes - a.usedMemoryBytes);

  return (
    <Card>
      <CardHeader title={`GPU ${gpu.index}`} subtitle="GPU 통합 정보" Icon={Microchip} />
      <div className="space-y-2.5">
        <MetricRow
          label="온도"
          value={formatPct(gpu.temperatureC, 1).replace('%', '°C')}
          sparkline={tempHist}
          timestamps={historyTimestamps}
          formatValue={(v) => `${v.toFixed(1)} °C`}
          tickSize={0.1}
        />
        <MetricRow
          label="전력량"
          value={gpu.powerW !== null ? `${gpu.powerW.toFixed(1)} W` : '—'}
          sparkline={powerHist}
          timestamps={historyTimestamps}
          formatValue={(v) => `${v.toFixed(1)} W`}
          tickSize={0.1}
        />
        <MetricRow
          label="연산 사용률"
          value={formatPct(gpu.utilizationPct)}
          sparkline={utilHist}
          timestamps={historyTimestamps}
          formatValue={(v) => `${v.toFixed(1)} %`}
          tickSize={0.1}
        />
        <div className="grid grid-cols-[60px_1fr_auto] items-center gap-3 text-xs">
          <span className="text-foreground-muted">VRAM</span>
          <VramStackBar totalBytes={gpu.memoryTotalBytes} processes={sortedProcesses} />
          <span className="font-medium tabular-nums whitespace-nowrap text-right min-w-15">
            {vramPct.toFixed(1)} %
          </span>
        </div>
      </div>

      {sortedProcesses.length > 0 ? (
        <div className="flex items-center justify-between text-xs text-foreground-muted mt-1 pt-2 border-t border-border">
          <span>프로세스</span>
          <span className="tabular-nums">{sortedProcesses.length}개</span>
        </div>
      ) : null}
      {sortedProcesses.length > 0 ? (
        // 5개 정확히 노출 (line-height 16px × 5 + gap 6px × 4 = 104px). 그 이상은 세로 스크롤.
        <ul className="space-y-1.5 max-h-26 overflow-y-auto pr-1">
          {sortedProcesses.map((p) => {
            const pct =
              gpu.memoryTotalBytes > 0 ? (p.usedMemoryBytes / gpu.memoryTotalBytes) * 100 : 0;
            const color = hashColor(`${p.name}-${p.identifier}`);
            return (
              <li
                key={`${p.pid}-${p.name}`}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className="size-2 rounded-full shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="truncate font-medium">{p.name}</span>
                  {p.identifier !== '<Unknown>' && p.identifier !== p.name ? (
                    <span className="text-foreground-muted truncate">({p.identifier})</span>
                  ) : null}
                </span>
                <span className="tabular-nums shrink-0">
                  <span className="font-medium">{pct.toFixed(1)} %</span>
                  <span className="text-foreground-muted ml-1.5">
                    ({formatBytes(p.usedMemoryBytes)})
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      <footer className="text-xs text-foreground-muted text-right mt-auto pt-2 border-t border-border">
        {formatBytes(gpu.memoryUsedBytes)} / {formatBytes(gpu.memoryTotalBytes)} (
        {formatBytes(freeBytes)} free)
      </footer>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────
// System Cards
// ───────────────────────────────────────────────────────────────

function CpuUtilCard({
  latest,
  history,
}: { latest: ResourceSnapshot; history: ResourceSnapshot[] }) {
  const hist = history.map((s) => s.cpu.utilizationPct ?? 0);
  const ts = history.map((s) => s.collectedAt);
  return (
    <Card>
      <CardHeader title="CPU" subtitle="CPU 사용률" Icon={Cpu} />
      <div className="flex items-end justify-between gap-3">
        <Sparkline
          values={hist}
          timestamps={ts}
          formatValue={(v) => `${v.toFixed(1)} %`}
          tickSize={0.1}
        />
        <span className="text-lg font-semibold tabular-nums whitespace-nowrap text-right min-w-22">
          {formatPct(latest.cpu.utilizationPct)}
        </span>
      </div>
      <footer className="text-xs text-foreground-muted text-right mt-auto pt-2">
        {latest.cpu.cores} Cores
      </footer>
    </Card>
  );
}

function CpuTempCard({
  latest,
  history,
}: { latest: ResourceSnapshot; history: ResourceSnapshot[] }) {
  const hist = history.map((s) => s.cpu.temperatureC ?? 0);
  const ts = history.map((s) => s.collectedAt);
  return (
    <Card>
      <CardHeader title="CPU" subtitle="CPU 온도" Icon={CpuIcon} />
      <div className="flex items-end justify-between gap-3">
        <Sparkline
          values={hist}
          timestamps={ts}
          formatValue={(v) => `${v.toFixed(1)} °C`}
          tickSize={0.1}
        />
        <span className="text-lg font-semibold tabular-nums whitespace-nowrap text-right min-w-22">
          {latest.cpu.temperatureC !== null ? `${latest.cpu.temperatureC.toFixed(1)} °C` : '—'}
        </span>
      </div>
      <footer className="text-xs text-foreground-muted text-right mt-auto pt-2">
        {latest.cpu.cores} Cores
      </footer>
    </Card>
  );
}

function MemoryCard({
  latest,
  history,
}: { latest: ResourceSnapshot; history: ResourceSnapshot[] }) {
  const hist = history.map((s) =>
    s.memory.totalBytes > 0 ? (s.memory.usedBytes / s.memory.totalBytes) * 100 : 0,
  );
  const ts = history.map((s) => s.collectedAt);
  const pct =
    latest.memory.totalBytes > 0 ? (latest.memory.usedBytes / latest.memory.totalBytes) * 100 : 0;
  const free = Math.max(0, latest.memory.totalBytes - latest.memory.usedBytes);
  return (
    <Card>
      <CardHeader title="Memory" subtitle="메모리 사용률" Icon={MemoryStick} />
      <div className="flex items-end justify-between gap-3">
        <Sparkline
          values={hist}
          timestamps={ts}
          formatValue={(v) => `${v.toFixed(1)} %`}
          tickSize={0.1}
        />
        <span className="text-lg font-semibold tabular-nums whitespace-nowrap text-right min-w-22">
          {pct.toFixed(1)} %
        </span>
      </div>
      <footer className="text-xs text-foreground-muted text-right mt-auto pt-2">
        {formatBytes(latest.memory.usedBytes)} / {formatBytes(latest.memory.totalBytes)} (
        {formatBytes(free)} free)
      </footer>
    </Card>
  );
}

const DISK_WARNING_PCT = 60;

function DiskCard({ latest, history }: { latest: ResourceSnapshot; history: ResourceSnapshot[] }) {
  const hist = history.map((s) =>
    s.disk.totalBytes > 0 ? (s.disk.usedBytes / s.disk.totalBytes) * 100 : 0,
  );
  const ts = history.map((s) => s.collectedAt);
  const pct =
    latest.disk.totalBytes > 0 ? (latest.disk.usedBytes / latest.disk.totalBytes) * 100 : 0;
  const free = Math.max(0, latest.disk.totalBytes - latest.disk.usedBytes);
  const warn = pct >= DISK_WARNING_PCT;
  return (
    <Card>
      <CardHeader title="Disk" subtitle="디스크 사용률" Icon={HardDrive} />
      <div className="flex items-end justify-between gap-3">
        <Sparkline
          values={hist}
          timestamps={ts}
          color={warn ? '#f59e0b' : undefined}
          formatValue={(v) => `${v.toFixed(1)} %`}
          tickSize={0.1}
        />
        <span
          className={cn(
            'text-lg font-semibold tabular-nums whitespace-nowrap text-right min-w-22',
            warn && 'text-amber-500',
          )}
        >
          {pct.toFixed(1)} %
        </span>
      </div>
      <footer className="text-xs text-foreground-muted text-right mt-auto pt-2">
        {formatBytes(latest.disk.usedBytes)} / {formatBytes(latest.disk.totalBytes)} (
        {formatBytes(free)} free)
      </footer>
    </Card>
  );
}
